import type {
  ApiSuccess,
  AuthResult,
  AuthSession,
  BoardDto,
  ConnectionDto,
  ConnectorDto,
  DataJob,
  FollowupDto,
  InvitePreview,
  LeadDetailDto,
  MemberDto,
  OtpChallenge,
  PendingProposalPage,
  ProposalDto,
  ScheduledActionDto,
  ScheduledMessageDto,
  SignupResult,
  SignupSession,
  TeamMemberDto,
  TemplateDto,
  TodayDto,
  WorkspaceData,
} from "@/shared/contracts";
import type {
  AcceptInviteInput,
  AiFeedbackNoteInput,
  AiFeedbackSummaryDto,
  AiFocus,
  AiHealthDto,
  AiModelChoice,
  AiTextEditRequest,
  AiTextEditResult,
  ApiCostsDto,
  ApiUsagePageDto,
  AppointmentConflictDto,
  AppointmentDto,
  AppointmentInput,
  AppointmentListDto,
  AppointmentUpdateInput,
  CallFinishInput,
  CallRecordingDto,
  CallStartInput,
  CallSummaryContent,
  ConversionSuggestionDecisionInput,
  EventDto,
  FollowupDecision,
  FollowupPatch,
  FollowupPrepareInput,
  FollowupScheduleInput,
  InviteInput,
  LeadArchiveInput,
  LeadBatchArchiveInput,
  LeadBatchRestoreInput,
  LeadBatchResultDto,
  LeadCloseStage,
  LeadConversionInput,
  LeadConversionUndoInput,
  LeadConversionsDto,
  LeadPatch,
  LeadRestoreInput,
  LeadRoutingDto,
  LeadRoutingInput,
  LeadSiteDto,
  LeadSpamInput,
  LeadStage,
  LeadTurnInput,
  LeadUpsellDto,
  LeadUpsellInput,
  ManualLeadInput,
  ManualMessageInput,
  MediaMessageMeta,
  MemoDto,
  MemoInput,
  MemoListDto,
  MemoUpdateInput,
  MergeLeadInput,
  MessageReactionInput,
  MessagingPagesDto,
  MessagingSetupDto,
  MetaConversionsDto,
  MetaConversionsInput,
  ProposalCategory,
  ProposalPatch,
  ProposalRejectInput,
  ProposalReviseInput,
  ProposalSourcesDto,
  ProposalSourcesExamplesDto,
  ProposalSourcesTodayDto,
  SitywebSite,
  StatisticsAdsAccountInput,
  StatisticsDto,
  StatisticsQuery,
  StatisticsSourcesDto,
  TemplateInput,
  TemplateUpdateInput,
  TemplateUseInput,
  WhatsappCostsDto,
  WikiPageCreate,
  WikiPageDto,
  WikiPageMove,
  WikiPageUpdate,
  WikiRevisionDto,
  WikiSearchResultDto,
  WikiTreeDto,
  WorkspaceConfigInput,
} from "@repo/polaris-shared";
import {
  STATISTICS_CHANNELS_MEDIA_TYPE,
  statisticsDtoSchema,
  statisticsSourcesDtoSchema,
} from "@repo/polaris-shared";
import { z } from "zod";

const API_ORIGIN = (import.meta.env.VITE_POLARIS_API_URL ?? "").replace(/\/$/, "");

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | null;

  constructor(
    message: string,
    status: number,
    code = "request_failed",
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

/** Another WhatsApp send of the same lead is still running: worth retrying shortly. */
export function isSendBusy(error: unknown): boolean {
  return error instanceof ApiError && error.code === "ACTION_ALREADY_RUNNING";
}

function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined | null>,
) {
  const url = new URL(`${API_ORIGIN}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== "")
        url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  query?: Record<string, string | number | boolean | undefined | null>,
): Promise<T> {
  if (
    options.method &&
    !["GET", "HEAD"].includes(options.method.toUpperCase()) &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    throw new ApiError(
      "Sei offline. La richiesta non è stata inviata: ricollegati e riprova.",
      0,
      "OFFLINE",
    );
  }
  // A form (the audio of a recorded call) goes as it is: the browser sets its boundary.
  const form = options.body instanceof FormData ? options.body : null;
  const response = await fetch(buildUrl(path, query), {
    ...options,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(options.body !== undefined && !form ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
    body: form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  });

  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => null)) as
    | { error?: { message?: string; code?: string; correlationId?: string }; data?: T }
    | { message?: string; code?: string }
    | T
    | null;
  if (!response.ok) {
    const structured =
      payload && typeof payload === "object" && "error" in payload ? payload.error : undefined;
    const direct =
      payload && typeof payload === "object" && "message" in payload ? payload : undefined;
    throw new ApiError(
      structured?.message ??
        direct?.message ??
        "Non siamo riusciti a completare la richiesta. Riprova.",
      response.status,
      structured?.code ?? direct?.code,
      structured?.correlationId ?? response.headers.get("x-correlation-id"),
    );
  }
  if (payload && typeof payload === "object" && "data" in payload && payload.data !== undefined) {
    return payload.data;
  }
  return payload as T;
}

/**
 * A file with its upload progress (fetch has none): the photos and voice notes of
 * the chat, so the operator sees how far a slow connection has got (T-PL-37).
 */
function uploadForm<T>(path: string, form: FormData, onProgress?: (share: number) => void) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return Promise.reject(
      new ApiError("Sei offline. Il file non è partito: ricollegati e riprova.", 0, "OFFLINE"),
    );
  }
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUrl(path));
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onerror = () =>
      reject(
        new ApiError("Connessione interrotta durante l'invio del file. Riprova.", 0, "NETWORK"),
      );
    xhr.onload = () => {
      let payload: { error?: { message?: string; code?: string; correlationId?: string } } | null =
        null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(payload as T);
      reject(
        new ApiError(
          payload?.error?.message ?? "Il file non è partito. Riprova.",
          xhr.status,
          payload?.error?.code,
          payload?.error?.correlationId ?? xhr.getResponseHeader("x-correlation-id"),
        ),
      );
    };
    xhr.send(form);
  });
}

async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(buildUrl(path), {
    credentials: "include",
    headers: { Accept: "application/json, application/octet-stream" },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: { message?: string; code?: string; correlationId?: string };
    } | null;
    throw new ApiError(
      payload?.error?.message ?? "Non siamo riusciti a scaricare l'esportazione.",
      response.status,
      payload?.error?.code,
      payload?.error?.correlationId ?? response.headers.get("x-correlation-id"),
    );
  }
  return response.blob();
}

function workspacePath(slug: string, suffix = "") {
  return `/api/workspaces/${encodeURIComponent(slug)}${suffix}`;
}

/** Address of a file of the workspace (same one the chat shows). */
export function mediaUrl(slug: string, attachmentId: string): string {
  return `${API_ORIGIN}${workspacePath(slug, `/media/${encodeURIComponent(attachmentId)}`)}`;
}

export const api = {
  conversions: {
    get: (slug: string, leadId: string) =>
      request<LeadConversionsDto>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/conversions`),
      ),
    record: (slug: string, leadId: string, input: LeadConversionInput) =>
      request<LeadConversionsDto>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/conversions`),
        {
          method: "POST",
          body: input,
        },
      ),
    undo: (slug: string, leadId: string, input: LeadConversionUndoInput) =>
      request<LeadConversionsDto>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/conversions/undo`),
        {
          method: "POST",
          body: input,
        },
      ),
    decide: (
      slug: string,
      leadId: string,
      suggestionId: string,
      input: ConversionSuggestionDecisionInput,
    ) =>
      request<LeadConversionsDto>(
        workspacePath(
          slug,
          `/leads/${encodeURIComponent(leadId)}/conversions/suggestions/${encodeURIComponent(suggestionId)}/decision`,
        ),
        {
          method: "POST",
          body: input,
        },
      ),
  },
  metaConversions: {
    get: (slug: string) => request<MetaConversionsDto>(workspacePath(slug, "/meta-conversions")),
    save: (slug: string, input: MetaConversionsInput) =>
      request<MetaConversionsDto>(workspacePath(slug, "/meta-conversions"), {
        method: "PUT",
        body: input,
      }),
    check: (slug: string) =>
      request<MetaConversionsDto>(workspacePath(slug, "/meta-conversions/check"), {
        method: "POST",
      }),
  },
  push: {
    config: async (slug: string) =>
      z
        .object({ enabled: z.boolean(), publicKey: z.string().nullable() })
        .parse(await request<unknown>(workspacePath(slug, "/push/config"))),
    subscribe: async (slug: string, subscription: PushSubscriptionJSON) =>
      z.object({ subscribed: z.literal(true) }).parse(
        await request<unknown>(workspacePath(slug, "/push/subscription"), {
          method: "PUT",
          body: { subscription },
        }),
      ),
    revoke: (slug: string, endpoint: string) =>
      request<void>(workspacePath(slug, "/push/subscription"), {
        method: "DELETE",
        body: { endpoint },
      }),
    test: async (slug: string, endpoint: string) =>
      z.object({ sent: z.literal(true) }).parse(
        await request<unknown>(workspacePath(slug, "/push/test"), {
          method: "POST",
          body: { endpoint },
        }),
      ),
  },
  auth: {
    session: () => request<AuthSession>("/api/me"),
    signInPassword: (email: string, password: string) =>
      request<AuthResult>("/auth/sign-in/email", { method: "POST", body: { email, password } }),
    sendOtp: (email: string) =>
      request<OtpChallenge>("/auth/email-otp/send-verification-otp", {
        method: "POST",
        body: { email, type: "sign-in" },
      }),
    signInOtp: (email: string, otp: string) =>
      request<AuthResult>("/auth/sign-in/email-otp", { method: "POST", body: { email, otp } }),
    signOut: () => request<ApiSuccess>("/auth/sign-out", { method: "POST" }),
    previewInvite: (token: string) =>
      request<InvitePreview>("/api/invitations/preview", {}, { token }),
    acceptInvite: (input: AcceptInviteInput) =>
      request<{ accepted: true; workspaceSlug: string }>("/api/invitations/accept", {
        method: "POST",
        body: input,
      }),
  },
  workspace: {
    bootstrap: (slug: string) => request<WorkspaceData>(workspacePath(slug, "/bootstrap")),
    updateConfig: (slug: string, input: WorkspaceConfigInput) =>
      request<{ saved: true }>(workspacePath(slug, "/config"), {
        method: "PUT",
        body: input,
      }),
    aiTextEdit: (slug: string, input: AiTextEditRequest) =>
      request<AiTextEditResult>(workspacePath(slug, "/ai/text-edit"), {
        method: "POST",
        body: input,
      }),
    exportData: (slug: string) =>
      request<{ id: string; status: "queued" }>(workspacePath(slug, "/data-export"), {
        method: "POST",
      }),
    dataJobs: (slug: string) => request<{ jobs: DataJob[] }>(workspacePath(slug, "/data-jobs")),
    downloadDataJob: (slug: string, id: string) =>
      requestBlob(workspacePath(slug, `/data-jobs/${encodeURIComponent(id)}/download`)),
    deleteData: (slug: string) =>
      request<{ id: string; status: "queued"; gracePeriodDays: number; scheduledFor: string }>(
        workspacePath(slug, "/data-deletion"),
        { method: "POST", body: { confirmation: "ELIMINA" } },
      ),
    cancelDataJob: (slug: string, id: string) =>
      request<{ cancelled: true }>(
        workspacePath(slug, `/data-jobs/${encodeURIComponent(id)}/cancel`),
        { method: "POST" },
      ),
  },
  /** Calls recorded from the lead page (ADR 0067). */
  calls: {
    start: (slug: string, leadId: string, input: CallStartInput) =>
      request<CallRecordingDto>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/calls`), {
        method: "POST",
        body: input,
      }),
    uploadPart: (slug: string, callId: string, seq: number, form: FormData) =>
      request<{ seq: number; stored: true }>(
        workspacePath(slug, `/calls/${encodeURIComponent(callId)}/parts/${seq}`),
        { method: "PUT", body: form },
      ),
    finish: (slug: string, callId: string, input: CallFinishInput) =>
      request<CallRecordingDto>(
        workspacePath(slug, `/calls/${encodeURIComponent(callId)}/finish`),
        {
          method: "POST",
          body: input,
        },
      ),
    cancel: (slug: string, callId: string) =>
      request<void>(workspacePath(slug, `/calls/${encodeURIComponent(callId)}`), {
        method: "DELETE",
      }),
    /** The operator's correction of the call summary (ADR 0067). */
    saveSummary: (slug: string, callId: string, input: CallSummaryContent) =>
      request<CallRecordingDto>(
        workspacePath(slug, `/calls/${encodeURIComponent(callId)}/summary`),
        { method: "PUT", body: input },
      ),
    /** "Crea riepilogo": one short AI call, only when the call has none. */
    createSummary: (slug: string, callId: string) =>
      request<CallRecordingDto>(
        workspacePath(slug, `/calls/${encodeURIComponent(callId)}/summary`),
        { method: "POST" },
      ),
  },
  leads: {
    list: (
      slug: string,
      filters: {
        search?: string;
        stage?: string;
        cursor?: string;
        assignedTo?: string;
        /** "customers": Customer Care (acquired contacts); "leads": everyone else. */
        view?: "leads" | "customers";
        /** Leave out the duplicates merged into another lead. */
        hideMerged?: boolean;
        /** "Tocca a me": only the leads waiting for the viewer. */
        myTurn?: boolean;
        /** From Statistiche: open leads never contacted ("none") or who never answered ("never"). */
        reply?: "none" | "never";
        /** Posta → Canale (ADR 0092). */
        channel?: "whatsapp" | "instagram" | "messenger";
        /** Posta → «Meno recenti»: the longest still first. */
        sort?: "oldest";
      },
    ) =>
      request<BoardDto>(
        workspacePath(slug, "/leads"),
        {},
        {
          q: filters.search,
          stage: filters.stage,
          view: filters.view,
          cursor: filters.cursor,
          assignedTo: filters.assignedTo,
          hideMerged: filters.hideMerged ? "1" : undefined,
          turn: filters.myTurn ? "me" : undefined,
          reply: filters.reply,
          channel: filters.channel,
          sort: filters.sort,
        },
      ),
    get: (slug: string, leadId: string) =>
      request<LeadDetailDto>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}`)),
    resolveSite: (slug: string, leadId: string, site: string) =>
      request<SitywebSite>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/sites/resolve`),
        {},
        { site },
      ),
    linkSite: (slug: string, leadId: string, site: string) =>
      request<LeadSiteDto>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/sites`), {
        method: "POST",
        body: { site },
      }),
    markRead: (slug: string, leadId: string) =>
      request<{ read: true }>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/read`), {
        method: "POST",
      }),
    create: (slug: string, input: ManualLeadInput) =>
      request<{ id: string; version: number }>(workspacePath(slug, "/leads"), {
        method: "POST",
        body: input,
      }),
    update: (slug: string, leadId: string, input: LeadPatch) =>
      request<{ version: number }>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}`), {
        method: "PATCH",
        body: input,
      }),
    merge: (slug: string, sourceLeadId: string, input: MergeLeadInput) =>
      request<{
        targetLeadId: string;
        targetVersion: number;
        sourceLeadId: string;
        sourceVersion: number;
      }>(workspacePath(slug, `/leads/${encodeURIComponent(sourceLeadId)}/merge`), {
        method: "POST",
        body: input,
      }),
    /** `closeAs`: "Archivia come perso / non qualificato" (ADR 0070). */
    archive: (slug: string, leadId: string, input: LeadArchiveInput = {}) =>
      request<void>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}`), {
        method: "DELETE",
        body: input,
      }),
    /** "Archivia" on a selection of Posta: what went and what did not, with the reason. */
    archiveMany: (slug: string, input: LeadBatchArchiveInput) =>
      request<LeadBatchResultDto>(workspacePath(slug, "/leads/archive"), {
        method: "POST",
        body: input,
      }),
    /** "Annulla" of `archiveMany` (`undo`), or "Ripristina" on a selection of Archiviati. */
    restoreMany: (slug: string, input: LeadBatchRestoreInput) =>
      request<LeadBatchResultDto>(workspacePath(slug, "/leads/restore"), {
        method: "POST",
        body: input,
      }),
    /** `undo`: "Annulla" right after archiving, the stage before comes back too. */
    restore: (slug: string, leadId: string, input: LeadRestoreInput = {}) =>
      request<{ version: number; stage: LeadStage }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/restore`),
        { method: "POST", body: input },
      ),
    /** «Spam» and «Non è spam» in the chat (ADR 0093). */
    spam: (slug: string, leadId: string, input: LeadSpamInput) =>
      request<{ version: number }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/spam`),
        { method: "POST", body: input },
      ),
    /** The switch "Tocca a me" in the chat (ADR 0083). */
    turn: (slug: string, leadId: string, input: LeadTurnInput) =>
      request<{ myTurn: boolean }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/turn`),
        { method: "POST", body: input },
      ),
    /** "Upsell" in a customer's chat: on with an optional note, or off (ADR 0065). */
    upsell: (slug: string, leadId: string, input: LeadUpsellInput) =>
      request<{ upsell: LeadUpsellDto | null; version: number }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/upsell`),
        { method: "POST", body: input },
      ),
    react: (slug: string, leadId: string, messageId: string, input: MessageReactionInput) =>
      request<{ actionId: string; messageId: string; status: string }>(
        workspacePath(
          slug,
          `/leads/${encodeURIComponent(leadId)}/messages/${encodeURIComponent(messageId)}/reaction`,
        ),
        { method: "POST", body: input },
      ),
    /** Text copied from WhatsApp for a message Meta did not hand over; "" removes it (ADR 0052). */
    setMessageText: (slug: string, leadId: string, messageId: string, text: string) =>
      request<{ id: string; text: string | null }>(
        workspacePath(
          slug,
          `/leads/${encodeURIComponent(leadId)}/messages/${encodeURIComponent(messageId)}/text`,
        ),
        { method: "PUT", body: { text } },
      ),
    /** A photo, video, document or voice note from the chat (T-PL-37, ADR 0086). */
    sendMedia: (
      slug: string,
      leadId: string,
      input: { file: Blob; fileName: string; meta: MediaMessageMeta },
      onProgress?: (share: number) => void,
    ) => {
      const form = new FormData();
      form.append("meta", JSON.stringify(input.meta));
      form.append("file", input.file, input.fileName);
      return uploadForm<{ actionId: string; messageId: string; status: string }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/messages/media`),
        form,
        onProgress,
      );
    },
    sendMessage: (slug: string, leadId: string, input: ManualMessageInput) =>
      request<{ actionId: string; messageId: string; status: string }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/messages`),
        {
          method: "POST",
          body: input,
        },
      ),
    generateProposal: (
      slug: string,
      leadId: string,
      options: {
        focus?: AiFocus;
        instructions?: string;
        triggerMessageId?: string | null;
        model?: AiModelChoice;
      } = {},
    ) =>
      request<ApiSuccess>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/ai-proposals`),
        {
          method: "POST",
          body: {
            focus: options.focus ?? "all",
            ...(options.instructions?.trim() ? { instructions: options.instructions.trim() } : {}),
            // "Riprova" asks again about the same message (ADR 0072).
            ...(options.triggerMessageId ? { triggerMessageId: options.triggerMessageId } : {}),
            ...(options.model ? { model: options.model } : {}),
          },
        },
      ),
    /** "Genera ora": the automatic proposal starts without waiting for the lead to stop writing. */
    startProposalNow: (slug: string, leadId: string) =>
      request<{ status: "started" | "running" }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/ai-proposals/now`),
        { method: "POST" },
      ),
    /** "Ignora": the chat stays without the proposal that failed; the banner lets it go. */
    dismissFailedProposal: (slug: string, leadId: string) =>
      request<{ status: "dismissed" }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/ai-proposals/failed`),
        { method: "DELETE" },
      ),
    /** "Annulla": the automatic proposal waiting for the lead to stop writing is dropped. */
    cancelWaitingProposal: (slug: string, leadId: string) =>
      request<{ status: "cancelled" }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/ai-proposals/waiting`),
        { method: "DELETE" },
      ),
    scheduledActions: (slug: string, leadId: string) =>
      request<{ actions: ScheduledActionDto[] }>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/actions`),
      ).then((result) => result.actions),
    cancelScheduledAction: (slug: string, leadId: string, actionId: string) =>
      request<{ cancelled: true }>(
        workspacePath(
          slug,
          `/leads/${encodeURIComponent(leadId)}/actions/${encodeURIComponent(actionId)}`,
        ),
        { method: "DELETE" },
      ),
    /** A programmed WhatsApp message at another time, or "now" ("Invia ora"). */
    rescheduleAction: (slug: string, leadId: string, actionId: string, scheduledFor: string) =>
      request<{ actionId: string; scheduledFor: string }>(
        workspacePath(
          slug,
          `/leads/${encodeURIComponent(leadId)}/actions/${encodeURIComponent(actionId)}/reschedule`,
        ),
        { method: "POST", body: { scheduledFor } },
      ),
  },
  /** "Programmati" in the calendar: the workspace's messages waiting for their time. */
  scheduledMessages: {
    list: (slug: string) =>
      request<{ messages: ScheduledMessageDto[] }>(workspacePath(slug, "/scheduled-messages")).then(
        (result) => result.messages,
      ),
  },
  followups: {
    today: (slug: string, filters: { assignedTo?: string } = {}) =>
      request<TodayDto>(workspacePath(slug, "/today"), {}, { assignedTo: filters.assignedTo }),
    schedule: (slug: string, leadId: string, input: FollowupScheduleInput) =>
      request<FollowupDto>(workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/followups`), {
        method: "POST",
        body: input,
      }),
    update: (slug: string, followupId: string, input: FollowupPatch) =>
      request<FollowupDto>(workspacePath(slug, `/followups/${encodeURIComponent(followupId)}`), {
        method: "PATCH",
        body: input,
      }),
    done: (slug: string, followupId: string, version: number) =>
      request<FollowupDto>(
        workspacePath(slug, `/followups/${encodeURIComponent(followupId)}/done`),
        { method: "POST", body: { version } },
      ),
    skip: (slug: string, followupId: string, version: number) =>
      request<FollowupDto>(
        workspacePath(slug, `/followups/${encodeURIComponent(followupId)}/skip`),
        { method: "POST", body: { version } },
      ),
    decide: (
      slug: string,
      followupId: string,
      version: number,
      action: FollowupDecision,
      closeAs?: LeadCloseStage,
      details: Omit<LeadArchiveInput, "closeAs"> = {},
    ) =>
      request<FollowupDto>(
        workspacePath(slug, `/followups/${encodeURIComponent(followupId)}/decide`),
        { method: "POST", body: { version, action, ...(closeAs ? { closeAs, ...details } : {}) } },
      ),
    /** "Prepara con AI": the AI decides now on a follow-up of the day (ADR 0055). */
    /** The AI prepares it now; with `text`, "Modifica con AI" rewrites that text (ADR 0061). */
    prepare: (slug: string, followupId: string, input: FollowupPrepareInput) =>
      request<ApiSuccess>(
        workspacePath(slug, `/followups/${encodeURIComponent(followupId)}/prepare`),
        { method: "POST", body: input },
      ),
  },
  /** "Memo" in the calendar: to remember every day until "Fatto" (ADR 0076). */
  memos: {
    /** `memberId`: a member's memos, or "all". */
    list: (slug: string, memberId: string) =>
      request<MemoListDto>(workspacePath(slug, "/memos"), {}, { memberId }),
    create: (slug: string, input: MemoInput) =>
      request<MemoDto>(workspacePath(slug, "/memos"), { method: "POST", body: input }),
    update: (slug: string, memoId: string, input: MemoUpdateInput) =>
      request<MemoDto>(workspacePath(slug, `/memos/${encodeURIComponent(memoId)}`), {
        method: "PATCH",
        body: input,
      }),
    postpone: (slug: string, memoId: string, input: { version: number; startsOn: string }) =>
      request<MemoDto>(workspacePath(slug, `/memos/${encodeURIComponent(memoId)}/postpone`), {
        method: "POST",
        body: input,
      }),
    done: (slug: string, memoId: string, version: number) =>
      request<MemoDto>(workspacePath(slug, `/memos/${encodeURIComponent(memoId)}/done`), {
        method: "POST",
        body: { version },
      }),
    remove: (slug: string, memoId: string, version: number) =>
      request<{ deleted: boolean }>(
        workspacePath(slug, `/memos/${encodeURIComponent(memoId)}`),
        { method: "DELETE" },
        { version: String(version) },
      ),
  },
  /** The operators' calendars (ADR 0059). */
  appointments: {
    list: (slug: string, range: { from: string; to: string; memberId: string }) =>
      request<AppointmentListDto>(workspacePath(slug, "/appointments"), {}, range),
    reminders: (slug: string) =>
      request<{ appointments: AppointmentDto[] }>(
        workspacePath(slug, "/appointments/reminders"),
      ).then((result) => result.appointments),
    conflicts: (
      slug: string,
      slot: { memberId: string; startsAt: string; endsAt: string; excludeId?: string | null },
    ) =>
      request<{ conflicts: AppointmentConflictDto[] }>(
        workspacePath(slug, "/appointments/conflicts"),
        {},
        slot,
      ).then((result) => result.conflicts),
    create: (slug: string, input: AppointmentInput) =>
      request<AppointmentDto>(workspacePath(slug, "/appointments"), {
        method: "POST",
        body: input,
      }),
    update: (slug: string, appointmentId: string, input: AppointmentUpdateInput) =>
      request<AppointmentDto>(
        workspacePath(slug, `/appointments/${encodeURIComponent(appointmentId)}`),
        { method: "PATCH", body: input },
      ),
  },
  proposals: {
    list: (
      slug: string,
      options: { cursor?: string; category?: ProposalCategory; assignedTo?: string } = {},
    ) =>
      request<{
        proposals: ProposalDto[];
        nextCursor: string | null;
        counts?: PendingProposalPage["counts"];
      }>(
        workspacePath(slug, "/proposals"),
        {},
        {
          status: "pending_approval",
          cursor: options.cursor,
          category: options.category,
          assignedTo: options.assignedTo,
        },
      ).then(
        (result): PendingProposalPage => ({
          items: result.proposals,
          nextCursor: result.nextCursor,
          counts: result.counts ?? null,
        }),
      ),
    update: (slug: string, proposalId: string, input: ProposalPatch) =>
      request<{ id: string; version: number }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}`),
        {
          method: "PATCH",
          body: input,
        },
      ),
    approve: (slug: string, proposalId: string, version: number, scheduledFor: string | null) =>
      request<{ actionId: string; status: string }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/approve`),
        {
          method: "POST",
          body: { version, scheduledFor },
        },
      ),
    /** "Modifica" of a programmed message: a new time, and a new text if it changed. */
    reschedule: (
      slug: string,
      proposalId: string,
      input: { version: number; scheduledFor: string; content?: string },
    ) =>
      request<{ proposalId: string; actionId: string }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/reschedule`),
        { method: "POST", body: input },
      ),
    reuse: (slug: string, proposalId: string, input: { version: number }) =>
      request<{ proposalId: string }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/reuse`),
        { method: "POST", body: input },
      ),
    reject: (slug: string, proposalId: string, input: ProposalRejectInput) =>
      request<{ rejected: true }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/reject`),
        {
          method: "POST",
          body: input,
        },
      ),
    /** "Rigenera con Sol": the same kind of proposal again, from the other model (ADR 0077). */
    regenerate: (slug: string, proposalId: string, model: AiModelChoice) =>
      request<{ jobId: string; status: "queued" }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/regenerate`),
        { method: "POST", body: { model } },
      ),
    /** "Correggi con AI": the AI prepares the proposal again following the request. */
    revise: (slug: string, proposalId: string, input: ProposalReviseInput) =>
      request<{ jobId: string; feedbackId: string; status: "queued" }>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/revise`),
        { method: "POST", body: input },
      ),
    /** The reply leaves from the operator's WhatsApp: its echo approves the proposal. */
    manualSend: (slug: string, proposalId: string, text: string) =>
      request<void>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/manual-send`),
        {
          method: "POST",
          body: { text },
        },
      ),
  },
  /** Observations on the AI proposals, read in the development pass (ADR 0051). */
  aiFeedback: {
    note: (slug: string, input: AiFeedbackNoteInput) =>
      request<{ id: string }>(workspacePath(slug, "/ai-feedback"), {
        method: "POST",
        body: input,
      }),
    summary: (slug: string) =>
      request<AiFeedbackSummaryDto>(workspacePath(slug, "/ai-feedback/summary")),
  },
  templates: {
    list: (slug: string) =>
      request<{ templates: TemplateDto[] }>(workspacePath(slug, "/templates")).then(
        (result) => result.templates,
      ),
    create: (slug: string, connectionId: string, input: TemplateInput) =>
      request<{ id: string; status: "draft" }>(workspacePath(slug, "/templates"), {
        method: "POST",
        body: { ...input, connectionId },
      }),
    submit: (slug: string, templateId: string) =>
      request<{ id: string; status: "pending" }>(
        workspacePath(slug, `/templates/${encodeURIComponent(templateId)}/submit`),
        { method: "POST" },
      ),
    sync: (slug: string, connectionId: string) =>
      request<ApiSuccess>(workspacePath(slug, "/templates/sync"), {
        method: "POST",
        body: { connectionId },
      }),
    /** "Modifica" (ADR 0061): a draft at once, a template on Meta back to review. */
    update: (slug: string, templateId: string, input: TemplateUpdateInput) =>
      request<{ id: string; status: "draft" | "queued" }>(
        workspacePath(slug, `/templates/${encodeURIComponent(templateId)}`),
        { method: "PATCH", body: input },
      ),
    /** "Sposta nei follow-up" / "Sposta in Altri" (ADR 0061): nothing changes on Meta. */
    setUse: (slug: string, templateId: string, input: TemplateUseInput) =>
      request<{ id: string; use: "followup" | "other" }>(
        workspacePath(slug, `/templates/${encodeURIComponent(templateId)}/use`),
        { method: "POST", body: input },
      ),
    /** "Elimina": a draft at once (`deleted`), a template on Meta once Meta confirms. */
    remove: (slug: string, templateId: string) =>
      request<{ id: string; deleted: boolean }>(
        workspacePath(slug, `/templates/${encodeURIComponent(templateId)}`),
        { method: "DELETE" },
      ),
  },
  /** The workspace wiki (ADR 0075). */
  wiki: {
    /** "Da dove viene" of a proposal. */
    proposalSources: (slug: string, proposalId: string) =>
      request<ProposalSourcesDto>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/sources`),
      ),
    /** The same search against today's wiki. */
    proposalSourcesToday: (slug: string, proposalId: string) =>
      request<ProposalSourcesTodayDto>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/sources/today`),
      ),
    /** The chats like this one whose replies could be examples (ADR 0073). */
    proposalSourcesExamples: (slug: string, proposalId: string) =>
      request<ProposalSourcesExamplesDto>(
        workspacePath(slug, `/proposals/${encodeURIComponent(proposalId)}/sources/examples`),
      ),
    tree: (slug: string) => request<WikiTreeDto>(workspacePath(slug, "/wiki")),
    search: (slug: string, query: string) =>
      request<{ results: WikiSearchResultDto[] }>(
        workspacePath(slug, "/wiki/search"),
        {},
        { q: query },
      ).then((result) => result.results),
    page: (slug: string, pageId: string) =>
      request<WikiPageDto>(workspacePath(slug, `/wiki/pages/${encodeURIComponent(pageId)}`)),
    create: (slug: string, input: WikiPageCreate) =>
      request<WikiPageDto>(workspacePath(slug, "/wiki/pages"), { method: "POST", body: input }),
    update: (slug: string, pageId: string, input: WikiPageUpdate) =>
      request<WikiPageDto>(workspacePath(slug, `/wiki/pages/${encodeURIComponent(pageId)}`), {
        method: "PATCH",
        body: input,
      }),
    move: (slug: string, pageId: string, input: WikiPageMove) =>
      request<void>(workspacePath(slug, `/wiki/pages/${encodeURIComponent(pageId)}/move`), {
        method: "POST",
        body: input,
      }),
    remove: (slug: string, pageId: string) =>
      request<{ deleted: number }>(
        workspacePath(slug, `/wiki/pages/${encodeURIComponent(pageId)}`),
        { method: "DELETE" },
      ),
    revisions: (slug: string, pageId: string) =>
      request<{ revisions: WikiRevisionDto[] }>(
        workspacePath(slug, `/wiki/pages/${encodeURIComponent(pageId)}/revisions`),
      ).then((result) => result.revisions),
    restore: (slug: string, pageId: string, revisionId: string, version: number) =>
      request<WikiPageDto>(
        workspacePath(
          slug,
          `/wiki/pages/${encodeURIComponent(pageId)}/revisions/${encodeURIComponent(revisionId)}/restore`,
        ),
        { method: "POST", body: { version } },
      ),
  },
  costs: {
    get: (slug: string, month: string | null) =>
      request<WhatsappCostsDto>(workspacePath(slug, "/whatsapp-costs"), {}, { month }),
    sync: (slug: string) =>
      request<{ status: "queued" }>(workspacePath(slug, "/whatsapp-costs/sync"), {
        method: "POST",
      }),
  },
  statistics: {
    get: (slug: string, filters: StatisticsQuery) =>
      request<StatisticsDto>(
        workspacePath(slug, "/statistics"),
        { headers: { Accept: STATISTICS_CHANNELS_MEDIA_TYPE } },
        filters,
      ).then((data) => statisticsDtoSchema.parse(data)),
    sources: (slug: string) =>
      request<StatisticsSourcesDto>(workspacePath(slug, "/statistics/sources")).then((data) =>
        statisticsSourcesDtoSchema.parse(data),
      ),
    saveAdsAccount: (slug: string, input: StatisticsAdsAccountInput) =>
      request<StatisticsSourcesDto>(workspacePath(slug, "/statistics/ads-account"), {
        method: "PUT",
        body: input,
      }).then((data) => statisticsSourcesDtoSchema.parse(data)),
    sync: (slug: string) =>
      request<{ status: "queued" }>(workspacePath(slug, "/statistics/sync"), {
        method: "POST",
        body: {},
      }),
  },
  /** The AI stopped for everyone and the chats left without a proposal (ADR 0072): owners and admins. */
  ai: {
    health: (slug: string) => request<AiHealthDto>(workspacePath(slug, "/ai/health")),
    retryFailed: (slug: string) =>
      request<{ queued: number }>(workspacePath(slug, "/ai/retry-failed"), { method: "POST" }),
  },
  /** OpenAI and Apify (ADR 0062): owners and admins only, like WhatsApp's. */
  apiCosts: {
    get: (slug: string, month: string | null) =>
      request<ApiCostsDto>(workspacePath(slug, "/api-costs"), {}, { month }),
    sync: (slug: string) =>
      request<{ status: "queued" }>(workspacePath(slug, "/api-costs/sync"), { method: "POST" }),
    leadUsage: (slug: string, leadId: string, cursor: string | null) =>
      request<ApiUsagePageDto>(
        workspacePath(slug, `/leads/${encodeURIComponent(leadId)}/api-usage`),
        {},
        { cursor },
      ),
  },
  integrations: {
    whatsappSignup: (slug: string) =>
      request<SignupSession>(workspacePath(slug, "/connections/whatsapp/state"), {
        method: "POST",
      }),
    completeWhatsappSignup: (slug: string, state: string, result: SignupResult) =>
      request<{ connectionId: string; status: "active" }>(
        workspacePath(slug, "/connections/whatsapp/complete"),
        { method: "POST", body: { state, ...result } },
      ),
    verify: (slug: string, connectionId: string) =>
      request<ApiSuccess>(
        workspacePath(slug, `/connections/${encodeURIComponent(connectionId)}/test`),
        {
          method: "POST",
        },
      ),
    leadAdsByo: (slug: string, input: Record<string, unknown>) =>
      request<{ connectionId: string; status: "active" }>(
        workspacePath(slug, "/connections/meta-leads"),
        {
          method: "POST",
          body: input,
        },
      ),
    leadAdsSync: (slug: string, connectionId: string) =>
      request<{ queued: true }>(
        workspacePath(slug, `/connections/${encodeURIComponent(connectionId)}/lead-ads/sync`),
        { method: "POST" },
      ),
    leadAdsSettings: (
      slug: string,
      connectionId: string,
      input: { allForms: boolean; version: number },
    ) =>
      request<{ connection: ConnectionDto }>(
        workspacePath(slug, `/connections/${encodeURIComponent(connectionId)}/lead-ads`),
        { method: "PATCH", body: input },
      ),
    leadAdsSetup: (slug: string) =>
      request<{ callbackUrl: string; verifyToken: string; subscribedField: "leadgen" }>(
        workspacePath(slug, "/connections/meta-leads/setup"),
      ),
    messagingSetup: (slug: string) =>
      request<MessagingSetupDto>(workspacePath(slug, "/connections/messaging/setup")),
    messagingPages: (slug: string, login: { code: string; redirectUri: string | null }) =>
      request<MessagingPagesDto>(workspacePath(slug, "/connections/messaging/pages"), {
        method: "POST",
        body: login.redirectUri ? login : { code: login.code },
      }),
    completeMessaging: (slug: string, input: { selectionId: string; pageId: string }) =>
      request<{ connectionId: string; instagram: boolean }>(
        workspacePath(slug, "/connections/messaging/complete"),
        { method: "POST", body: input },
      ),
    messagingSettings: (
      slug: string,
      connectionId: string,
      input: { humanAgent: boolean; version: number },
    ) =>
      request<{ version: number }>(
        workspacePath(slug, `/connections/${encodeURIComponent(connectionId)}/messaging`),
        { method: "PATCH", body: input },
      ),
    disconnect: (slug: string, connectionId: string) =>
      request<{ disconnected: true; providerUnsubscribed: boolean; warning: string | null }>(
        workspacePath(slug, `/connections/${encodeURIComponent(connectionId)}`),
        { method: "DELETE" },
      ),
    connectors: (slug: string) =>
      request<{ connectors: ConnectorDto[] }>(workspacePath(slug, "/connectors")).then(
        (result) => result.connectors,
      ),
    createConnector: (slug: string, input: Record<string, unknown>) =>
      request<{ id: string; status: "active" }>(workspacePath(slug, "/connectors"), {
        method: "POST",
        body: input,
      }),
    disconnectConnector: (slug: string, connectorId: string) =>
      request<{ disconnected: true }>(
        workspacePath(slug, `/connectors/${encodeURIComponent(connectorId)}`),
        { method: "DELETE" },
      ),
  },
  leadRouting: {
    get: (slug: string) => request<LeadRoutingDto>(workspacePath(slug, "/lead-routing")),
    save: (slug: string, input: LeadRoutingInput) =>
      request<LeadRoutingDto>(workspacePath(slug, "/lead-routing"), { method: "PUT", body: input }),
  },
  team: {
    list: async (slug: string) => {
      const [members, invitations] = await Promise.all([
        request<{ members: MemberDto[] }>(workspacePath(slug, "/members")),
        request<{
          invitations: Array<{
            id: string;
            email: string;
            role: MemberDto["role"];
            status: "pending" | "accepted" | "revoked" | "expired";
          }>;
        }>(workspacePath(slug, "/invitations")),
      ]);
      const pending: TeamMemberDto[] = invitations.invitations
        .filter((item) => item.status === "pending")
        .map((item) => ({
          id: item.id,
          userId: null,
          email: item.email,
          name: null,
          role: item.role,
          status: "invited",
          invitation: true,
        }));
      const active: TeamMemberDto[] = members.members;
      return [...active, ...pending];
    },
    invite: (slug: string, input: InviteInput) =>
      request<{ id: string; email: string; role: string; expiresAt: string }>(
        workspacePath(slug, "/invitations"),
        {
          method: "POST",
          body: input,
        },
      ),
    updateRole: (slug: string, memberId: string, role: string) =>
      request<MemberDto>(workspacePath(slug, `/members/${encodeURIComponent(memberId)}`), {
        method: "PATCH",
        body: { role },
      }),
    revoke: (slug: string, memberId: string, invitation = false) =>
      request<ApiSuccess>(
        workspacePath(
          slug,
          invitation
            ? `/invitations/${encodeURIComponent(memberId)}`
            : `/members/${encodeURIComponent(memberId)}`,
        ),
        {
          method: "DELETE",
        },
      ),
  },
};

export function workspaceEventsUrl(slug: string) {
  return buildUrl(workspacePath(slug, "/events"));
}

export function parseWorkspaceEvent(raw: string): EventDto | null {
  try {
    const value = JSON.parse(raw) as EventDto;
    return typeof value === "object" && value !== null && "type" in value ? value : null;
  } catch {
    return null;
  }
}

export function readableApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof TypeError)
    return "Non riusciamo a raggiungere Polaris Leads. Controlla la connessione e riprova.";
  return fallback;
}
